import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { TextField } from '@material-ui/core'
import Select from '../../molecules/Select'
import {
  formatBucketLabel,
  bucketToRange,
  labelForPeriod
} from './BlueprintTimeseriesLatchPicker'

/**
 * Per-period control surface for `BlueprintTimeseriesLatchPicker`.
 *
 * Designed to be mounted with `key={period}` on the parent side — the period
 * itself never changes from this component's perspective. That convention is
 * what lets us avoid all the "reset state on period change" effects: a period
 * switch unmounts this and remounts a fresh instance with zeroed local state.
 *
 * Per-period transient state (selectedBucket, fromDate, toDate) lives here.
 * The parent owns the period selection and is responsible for gating
 * `bounds` / `buckets` to `null` when its `rangeMeta` doesn't yet match the
 * current period — that keeps the inner component oblivious to staleness.
 *
 * Renders three mutually exclusive control surfaces depending on (period,
 * bucketCount, daily bounds availability):
 *   - non-day, multiple buckets   -> Select dropdown
 *   - non-day, single bucket      -> subtle static label
 *   - non-day, no buckets         -> "(no <period> data)" placeholder
 *   - day, has data               -> two date inputs (from / to)
 *   - day, no data                -> "(no daily data)" placeholder
 *   - data not yet loaded         -> renders nothing (waits for parent)
 */
function PerPeriodControls ({ period, bounds, buckets, onChange }) {
  const [selectedBucket, setSelectedBucket] = useState(null)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  const minDate = bounds?.minDate ?? null
  const maxDate = bounds?.maxDate ?? null

  // Daily fallback: until the user types something, the inputs display the
  // most recent date from the table. Render-time derivation — no seed effect,
  // and a fresh `maxDate` from a data refresh automatically propagates.
  const effectiveFrom = fromDate || maxDate || ''
  const effectiveTo = toDate || maxDate || ''

  // Bucket fallback: until the user picks something, the most recent bucket
  // is the active selection. Parent has already gated `buckets` to null when
  // the metadata doesn't match this period, so no stale-period guard needed.
  const effectiveBucket = period !== 'day' && buckets?.length
    ? (selectedBucket ?? buckets[0])
    : null

  const bucketOptions = useMemo(
    () => (buckets || []).map(b => ({ value: b, label: formatBucketLabel(b, period) })),
    [buckets, period]
  )

  // The one legitimate side effect: notify the parent renderer when the
  // resolved (period, range) pair changes. Fires once per logical user action
  // (period switch via remount, bucket pick, date edit) and once on mount
  // when defaults resolve.
  useEffect(() => {
    if (!onChange) return
    if (period === 'day') {
      if (!effectiveFrom || !effectiveTo) return
      onChange(period, { from: effectiveFrom, to: effectiveTo })
      return
    }
    if (!effectiveBucket) return
    const range = bucketToRange(effectiveBucket, period)
    if (range) onChange(period, range)
  }, [onChange, period, effectiveFrom, effectiveTo, effectiveBucket])

  const showBucketSelect = period !== 'day' && bucketOptions.length > 1
  const showStaticBucket = period !== 'day' && bucketOptions.length === 1
  // `buckets !== null` doubles as the "metadata loaded for this period" signal
  // because the parent passes null while the fetch is in flight or stale.
  const showEmptyBucket = period !== 'day' && buckets !== null && bucketOptions.length === 0
  const showEmptyDaily = period === 'day' && bounds !== null && !maxDate
  const showDailyPickers = period === 'day' && !showEmptyDaily

  return (
    <>
      {showBucketSelect
        ? (
          <Select
            value={effectiveBucket}
            onChange={setSelectedBucket}
            options={bucketOptions}
            placeholder='Select period'
          />
        )
        : null}

      {showStaticBucket && effectiveBucket
        ? (
          <span className='font-size-sm' style={{ color: 'var(--text-color-subtle)' }}>
            {formatBucketLabel(effectiveBucket, period)}
          </span>
        )
        : null}

      {showEmptyBucket
        ? (
          <span className='font-size-sm' style={{ color: 'var(--text-color-subtle)' }}>
            (no {labelForPeriod(period).toLowerCase()} data)
          </span>
        )
        : null}

      {showEmptyDaily
        ? (
          <span className='font-size-sm' style={{ color: 'var(--text-color-subtle)' }}>
            (no daily data)
          </span>
        )
        : null}

      {showDailyPickers
        ? (
          <>
            <TextField
              value={effectiveFrom}
              onChange={(e) => setFromDate(e.target.value)}
              size='small'
              type='date'
              inputProps={{ min: minDate || undefined, max: maxDate || undefined }}
            />
            <TextField
              value={effectiveTo}
              onChange={(e) => setToDate(e.target.value)}
              size='small'
              type='date'
              inputProps={{ min: minDate || undefined, max: maxDate || undefined }}
            />
          </>
        )
        : null}
    </>
  )
}

PerPeriodControls.propTypes = {
  period: PropTypes.string.isRequired,
  bounds: PropTypes.shape({
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
    rowCount: PropTypes.number
  }),
  buckets: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired
}

export default PerPeriodControls
